import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/features/shared/DataTable'
import { Collector, CollectorStatus, formatCollectorStatus } from '@/domain/collectors/collector'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { PaginationState } from '@tanstack/react-table'

interface CollectorTableProps {
  collectors: Collector[]
  totalCollectors?: number
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  searchKey: string
  searchPlaceholder: string
  onCreateCollector: () => void
  onEditCollector: (collector: Collector) => void
  onDeleteCollector: (collectorId: string) => void
}

export const CollectorTable = ({
  collectors,
  totalCollectors,
  pagination,
  onPaginationChange,
  searchKey,
  searchPlaceholder,
  onCreateCollector,
  onEditCollector,
  onDeleteCollector,
}: CollectorTableProps) => {
  const columns: ColumnDef<Collector>[] = [
    {
      accessorKey: 'code',
      header: 'Código',
      cell: ({ row }) => (
        <Badge variant="outline" className="bg-gray-50 font-mono">
          {row.original.code}
        </Badge>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Nombre',
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    // {
    //   accessorKey: 'email',
    //   header: 'Email',
    //   cell: ({ row }) => <div className="text-sm">{row.original.email || '-'}</div>,
    // },
    {
      accessorKey: 'docNumber',
      header: 'CUIT',
      cell: ({ row }) => <div className="text-sm">{row.original.docNumber || '-'}</div>,
    },
    {
      accessorKey: 'phone',
      header: 'Teléfono',
      cell: ({ row }) => <div className="text-sm">{row.original.phone || '-'}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const status = row.original.status
        return (
          <Badge variant="outline" className={status === CollectorStatus.Active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-600'}>
            {formatCollectorStatus(status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'commission',
      header: '% Cobro',
      cell: ({ row }) => <div className="text-sm">{row.original.commission}%</div>,
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const collector = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditCollector(collector)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeleteCollector(collector.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Cobradores</h2>
          <p className="text-sm text-muted-foreground">
            Personal responsable de la cobranza de pagos asignados
          </p>
        </div>
        <Button onClick={onCreateCollector}>Nuevo Colaborador</Button>
      </div>
      <DataTable
        columns={columns}
        data={collectors}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        totalRecords={totalCollectors}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
      />
    </div>
  )
}
